/* eslint-disable @typescript-eslint/no-explicit-any */
type EventKey = string | symbol;
type EventHandler<T = any> = (payload: T) => void;
type EventMap = Record<EventKey, EventHandler>;
type EventUnsubscribe = () => void;

export interface EventBus<T extends EventMap> {
    on<Key extends keyof T>(key: Key, handler: T[Key]): EventUnsubscribe;
    off<Key extends keyof T>(key: Key, handler: T[Key]): void;
    emit<Key extends keyof T>(
        key: Key,
        waitForSeats: boolean,
        ...payload: Parameters<T[Key]>
    ): void;
    once<Key extends keyof T>(key: Key, handler: T[Key]): void;
}

type Bus<E> = Record<keyof E, E[keyof E][]>;

/**
 * @example
 * interface SomeBus {
 *  'some-event': (payload: { detail: unknown }) => void
 * }
 *
 * const someBus = eventBus<SomeBus>()
 */
export function eventBus<E extends EventMap>(config?: {
    onError: (...params: any[]) => void;
}): EventBus<E> {
    const bus: Partial<Bus<E>> = {};

    const on: EventBus<E>['on'] = (key, handler) => {
        if (bus[key] === undefined) {
            bus[key] = [];
        }
        bus[key].push(handler);

        // unsubscribe function
        return () => {
            off(key, handler);
        };
    };

    const off: EventBus<E>['off'] = (key, handler) => {
        const index = bus[key]?.indexOf(handler) ?? -1;
        bus[key]?.splice(index >>> 0, 1);
    };

    const emit: EventBus<E>['emit'] = (key, waitForSeats, payload) => {
        const invokeFns = (seats) => {
            seats.forEach((fn) => {
                try {
                    fn?.(payload);
                } catch (e) {
                    config?.onError(e);
                }
            });
        };
        const seats = bus[key];

        if (!seats && waitForSeats) {
            let count = 0;
            const checkInterval = setInterval(() => {
                const seats = bus[key];

                if (seats || count === 5) {
                    clearInterval(checkInterval);
                    if (seats) {
                        invokeFns(seats);
                    }
                } else {
                    count++;
                }
            }, 500);
        } else if (seats) {
            invokeFns(seats);
        }
    };

    const once: EventBus<E>['once'] = (key, handler) => {
        const handleOnce = (payload: Parameters<typeof handler>) => {
            handler(payload);
            off(key, handleOnce as typeof handler);
        };

        on(key, handleOnce as typeof handler);
    };

    return { on, off, emit, once };
}
